import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Plus, 
  FileText, 
  Send, 
  Check, 
  X, 
  Trash2, 
  Edit, 
  Calculator,
  ArrowRight,
  Clock,
  Mail,
  Phone,
  Calendar,
  Users,
  Euro,
  Download,
  MessageCircle,
  Link,
  ExternalLink,
  Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminQuotes() {
  const { token } = useAuth();
  
  const [quotes, setQuotes] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    unit_id: '',
    data_arrivo: '',
    data_partenza: '',
    num_ospiti: 2,
    prezzo_notte: '',
    prezzo_totale: '',
    note: '',
    valid_until: ''
  });

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      const [quotesRes, unitsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQuotes(quotesRes.data);
      setUnits(unitsRes.data.filter(u => u.attivo));
    } catch (error) {
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!form.unit_id || !form.data_arrivo || !form.data_partenza) {
      toast.error('Seleziona unità e date per calcolare il prezzo');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/quotes/null/calculate-price`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            unit_id: form.unit_id,
            data_arrivo: form.data_arrivo,
            data_partenza: form.data_partenza
          }
        }
      );
      
      setCalculatedPrice(response.data);
      setForm({
        ...form,
        prezzo_notte: response.data.prezzo_notte_medio,
        prezzo_totale: response.data.prezzo_totale
      });
      toast.success(`Calcolato: €${response.data.prezzo_totale} per ${response.data.num_notti} notti`);
    } catch (error) {
      toast.error('Errore calcolo prezzo');
    }
  };

  const openNewQuote = () => {
    setEditingQuote(null);
    setForm({
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      unit_id: units[0]?.id || '',
      data_arrivo: '',
      data_partenza: '',
      num_ospiti: 2,
      prezzo_notte: '',
      prezzo_totale: '',
      note: '',
      valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    setCalculatedPrice(null);
    setDialogOpen(true);
  };

  const openEditQuote = (quote) => {
    setEditingQuote(quote);
    setForm({
      guest_name: quote.guest_name,
      guest_email: quote.guest_email || '',
      guest_phone: quote.guest_phone || '',
      unit_id: quote.unit_id,
      data_arrivo: quote.data_arrivo,
      data_partenza: quote.data_partenza,
      num_ospiti: quote.num_ospiti || 2,
      prezzo_notte: quote.prezzo_notte,
      prezzo_totale: quote.prezzo_totale,
      note: quote.note || '',
      valid_until: quote.valid_until || ''
    });
    setCalculatedPrice(null);
    setDialogOpen(true);
  };

  const saveQuote = async () => {
    if (!form.guest_name || !form.unit_id || !form.data_arrivo || !form.data_partenza || !form.prezzo_totale) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      if (editingQuote) {
        await axios.put(
          `${API}/admin/quotes/${editingQuote.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Preventivo aggiornato');
      } else {
        await axios.post(
          `${API}/admin/quotes`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Preventivo creato');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/quotes/${quoteId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Stato aggiornato a "${newStatus}"`);
      loadData();
    } catch (error) {
      toast.error('Errore aggiornamento stato');
    }
  };

  const convertToBooking = async (quoteId) => {
    try {
      const response = await axios.post(
        `${API}/admin/quotes/${quoteId}/convert-to-booking`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preventivo convertito in prenotazione!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore conversione');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Preventivo eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Download PDF
  const downloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(
        `${API}/admin/quotes/${quoteId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivo_${quoteId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF scaricato!');
    } catch (error) {
      toast.error('Errore generazione PDF');
    }
  };

  // Send email
  const sendEmail = async (quoteId, guestEmail) => {
    if (!guestEmail) {
      toast.error('Email ospite non disponibile');
      return;
    }
    
    if (!window.confirm(`Inviare il preventivo a ${guestEmail}?`)) return;
    
    try {
      await axios.post(
        `${API}/admin/quotes/${quoteId}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Preventivo inviato a ${guestEmail}!`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    }
  };

  // Open WhatsApp
  const openWhatsApp = async (quoteId) => {
    try {
      const response = await axios.get(
        `${API}/admin/quotes/${quoteId}/whatsapp-link`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Use browser's encodeURIComponent for better emoji support
      const message = response.data.message_raw || response.data.message;
      const phone = (response.data.phone || '').replace(/\s/g, '').replace('+', '');
      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      
      // Update status to sent
      await updateStatus(quoteId, 'sent');
    } catch (error) {
      toast.error('Errore generazione link WhatsApp');
    }
  };

  // Copy quote link to clipboard
  const copyQuoteLink = (quoteId) => {
    const link = `${window.location.origin}/preventivo/${quoteId}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Link copiato negli appunti!'))
      .catch(() => toast.error('Errore copia link'));
  };

  // Open quote page in new tab
  const openQuotePage = (quoteId) => {
    const link = `${window.location.origin}/preventivo/${quoteId}`;
    window.open(link, '_blank');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-yellow-100 text-yellow-700',
      converted: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      draft: 'Bozza',
      sent: 'Inviato',
      accepted: 'Accettato',
      rejected: 'Rifiutato',
      expired: 'Scaduto',
      converted: 'Convertito'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <AdminLayout title="Preventivi">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Preventivi">
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Preventivi</h1>
            <p className="text-gray-500">Gestisci i preventivi per i potenziali ospiti</p>
          </div>
          <Button onClick={openNewQuote} className="bg-[#C5A059] hover:bg-[#B08C4A]" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Preventivo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {['draft', 'sent', 'accepted', 'rejected', 'converted'].map(status => {
            const count = quotes.filter(q => q.status === status).length;
            const labels = { draft: 'Bozze', sent: 'Inviati', accepted: 'Accettati', rejected: 'Rifiutati', converted: 'Convertiti' };
            const colors = { draft: 'bg-gray-50', sent: 'bg-blue-50', accepted: 'bg-green-50', rejected: 'bg-red-50', converted: 'bg-purple-50' };
            return (
              <Card key={status} className={colors[status]}>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm text-gray-600">{labels[status]}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quotes List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Lista Preventivi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun preventivo creato</p>
                <Button onClick={openNewQuote} variant="outline" className="mt-4">
                  Crea il primo preventivo
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ospite</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unità</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Totale</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {quotes.map(quote => (
                      <tr key={quote.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="font-medium">{quote.guest_name}</div>
                          {quote.guest_email && (
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {quote.guest_email}
                            </div>
                          )}
                          {quote.guest_phone && (
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {quote.guest_phone}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">{quote.unit_name}</td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            {formatDate(quote.data_arrivo)} - {formatDate(quote.data_partenza)}
                          </div>
                          <div className="text-xs text-gray-500">{quote.num_notti} notti</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-bold text-lg">€{(quote.totale_complessivo || quote.prezzo_totale)?.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">Soggiorno: €{quote.prezzo_totale?.toFixed(2)}</div>
                          {quote.totale_servizi > 0 && (
                            <div className="text-xs text-green-600 mt-1 font-medium">
                              + €{quote.totale_servizi?.toFixed(2)} servizi
                            </div>
                          )}
                          {/* Dettaglio servizi */}
                          {quote.servizi_dettaglio && quote.servizi_dettaglio.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {quote.servizi_dettaglio.map((s, idx) => (
                                <div key={idx} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded flex justify-between">
                                  <span>{s.quantita}x {s.nome}</span>
                                  <span className="font-medium">€{s.totale?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">{getStatusBadge(quote.status)}</td>
                        <td className="px-4 py-4">
                          <div className="text-sm">{formatDate(quote.valid_until)}</div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {/* Link Preview - sempre disponibile */}
                            <Button size="sm" variant="outline" onClick={() => openQuotePage(quote.id)} title="Apri Pagina Preventivo" data-testid="btn-open-public-quote">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => copyQuoteLink(quote.id)} title="Copia Link" data-testid="btn-copy-quote-link">
                              <Copy className="w-4 h-4" />
                            </Button>
                            {/* PDF Download - sempre disponibile */}
                            <Button size="sm" variant="outline" onClick={() => downloadPDF(quote.id)} title="Scarica PDF" data-testid="btn-download-pdf">
                              <Download className="w-4 h-4" />
                            </Button>
                            
                            {/* Actions based on status */}
                            {quote.status === 'draft' && (
                              <>
                                <Button size="sm" variant="outline" className="text-blue-600" onClick={() => sendEmail(quote.id, quote.guest_email)} title="Invia Email" data-testid="btn-send-email">
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => openWhatsApp(quote.id)} title="Invia WhatsApp" data-testid="btn-send-whatsapp">
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openEditQuote(quote)} title="Modifica" data-testid="btn-edit-quote">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {quote.status === 'sent' && (
                              <>
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateStatus(quote.id, 'accepted')} title="Accetta" data-testid="btn-accept-quote">
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => updateStatus(quote.id, 'rejected')} title="Rifiuta" data-testid="btn-reject-quote">
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {quote.status === 'accepted' && !quote.booking_id && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => convertToBooking(quote.id)} title="Converti in prenotazione" data-testid="btn-convert-to-booking">
                                <ArrowRight className="w-4 h-4 mr-1" /> Prenota
                              </Button>
                            )}
                            {quote.status !== 'converted' && (
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteQuote(quote.id)} title="Elimina" data-testid="btn-delete-quote">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New/Edit Quote Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C5A059]" />
                {editingQuote ? 'Modifica Preventivo' : 'Nuovo Preventivo'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Guest Info */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Dati Ospite</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Nome Ospite *</Label>
                    <Input
                      value={form.guest_name}
                      onChange={(e) => setForm({...form, guest_name: e.target.value})}
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.guest_email}
                      onChange={(e) => setForm({...form, guest_email: e.target.value})}
                      placeholder="mario@email.com"
                    />
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input
                      value={form.guest_phone}
                      onChange={(e) => setForm({...form, guest_phone: e.target.value})}
                      placeholder="+39 333 1234567"
                    />
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="space-y-4 md:col-span-2">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Dettagli Soggiorno</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Unità *</Label>
                    <select
                      value={form.unit_id}
                      onChange={(e) => setForm({...form, unit_id: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="">Seleziona...</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Check-in *</Label>
                    <Input
                      type="date"
                      value={form.data_arrivo}
                      onChange={(e) => setForm({...form, data_arrivo: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Check-out *</Label>
                    <Input
                      type="date"
                      value={form.data_partenza}
                      onChange={(e) => setForm({...form, data_partenza: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>N. Ospiti</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={form.num_ospiti}
                      onChange={(e) => setForm({...form, num_ospiti: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-4 md:col-span-2">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-semibold text-gray-700">Prezzo</h3>
                  <Button variant="outline" size="sm" onClick={calculatePrice}>
                    <Calculator className="w-4 h-4 mr-2" />
                    Calcola da Tariffe
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Prezzo/Notte (€)</Label>
                    <Input
                      type="number"
                      value={form.prezzo_notte}
                      onChange={(e) => setForm({...form, prezzo_notte: parseFloat(e.target.value)})}
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <Label>Totale (€) *</Label>
                    <Input
                      type="number"
                      value={form.prezzo_totale}
                      onChange={(e) => setForm({...form, prezzo_totale: parseFloat(e.target.value)})}
                      placeholder="500"
                      className="text-lg font-bold"
                    />
                  </div>
                  <div>
                    <Label>Valido fino a</Label>
                    <Input
                      type="date"
                      value={form.valid_until}
                      onChange={(e) => setForm({...form, valid_until: e.target.value})}
                    />
                  </div>
                </div>
                
                {calculatedPrice && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <p><strong>Calcolo automatico:</strong></p>
                    <p>{calculatedPrice.num_notti} notti × €{calculatedPrice.prezzo_notte_medio}/notte = <strong>€{calculatedPrice.prezzo_totale}</strong></p>
                  </div>
                )}
              </div>

              {/* Note */}
              <div className="md:col-span-2">
                <Label>Note</Label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({...form, note: e.target.value})}
                  className="w-full border rounded-md p-2 h-24"
                  placeholder="Note aggiuntive per il preventivo..."
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={saveQuote} className="bg-[#C5A059] hover:bg-[#B08C4A]">
                {editingQuote ? 'Salva Modifiche' : 'Crea Preventivo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
